import React, { useState, useEffect, useRef, useId } from 'react';
import axios from 'axios';
import { Search, X, Loader, AlertCircle } from 'lucide-react';
import cie10Service from '../../services/cie10Service';

// axios rechaza una petición abortada con CanceledError, no con AbortError; se
// aceptan ambos (y axios.isCancel) para no confundir una cancelación con un fallo.
const esCancelacion = (err) =>
  axios.isCancel(err) || err?.name === 'CanceledError' || err?.name === 'AbortError';

/**
 * Componente de autocompletado para códigos CIE-10
 * 
 * Props:
 * - value: string - Valor actual (código o texto libre)
 * - onChange: function(value, selectedCode) - Callback cuando cambia el valor
 * - placeholder: string - Texto del placeholder
 * - type: 'diagnosis' | 'symptom' | null - Filtrar por tipo
 * - disabled: boolean - Deshabilitar input
 * - label: string - Etiqueta del campo
 * - required: boolean - Campo requerido
 */
function Cie10Autocomplete({
  value = '',
  onChange,
  placeholder = 'Buscar en CIE-10 o escribir manualmente...',
  type = null,
  disabled = false,
  label = 'Diagnóstico',
  required = false,
}) {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  
  const debounceTimerRef = useRef(null);
  const abortControllerRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Ids únicos por instancia: el formulario de historia clínica monta varios
  // autocompletados y con ids fijos el <label>, aria-controls y
  // aria-activedescendant apuntaban todos al primero.
  const baseId = useId();
  const inputId = `${baseId}-input`;
  const listboxId = `${baseId}-listbox`;
  const optionId = (index) => `${baseId}-option-${index}`;

  // Cancela la búsqueda que espera el debounce y la que ya está en vuelo.
  const cancelarBusqueda = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setLoading(false);
  };

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      // Cancel any pending API requests
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Sincronizar valor externo con estado interno
  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value);
      // Reset related state when external value changes
      if (!value) {
        setSelectedCode(null);
        setIsManualEntry(false);
      }
    }
  }, [value, inputValue]);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Búsqueda con debounce y AbortController
  const searchCie10 = async (query) => {
    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    if (!query || query.trim().length < 2) {
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    const controller = new AbortController();
    abortControllerRef.current = controller;
    setLoading(true);
    setError('');

    try {
      const response = await cie10Service.search(
        {
          q: query,
          limit: 15,
          type,
        },
        { signal: controller.signal }
      );

      // Si mientras tanto se eligió una sugerencia o se limpió el campo, esta
      // respuesta ya no interesa: no debe reabrir el desplegable.
      if (controller.signal.aborted) return;

      const results = response.data || [];
      
      // Agregar opción "Otro" al final
      setSuggestions([
        ...results,
        {
          _id: 'manual',
          code: 'MANUAL',
          description: '🖊️ Otro (escribir manualmente)',
          isManual: true,
        },
      ]);
      setShowDropdown(true);
      setHighlightedIndex(-1);
    } catch (err) {
      // Una búsqueda cancelada (el médico siguió tecleando) no es un error.
      if (esCancelacion(err) || controller.signal.aborted) {
        return;
      }
      console.error('❌ Error buscando CIE-10:', err);
      setError('Error al buscar códigos CIE-10');
      setSuggestions([]);
    } finally {
      // Solo la búsqueda vigente apaga el spinner: si esta ya fue reemplazada,
      // hay otra en curso y su indicador de carga debe seguir visible.
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
        setLoading(false);
      }
    }
  };

  // Handler de cambio de input
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsManualEntry(false);
    setSelectedCode(null);

    // Limpiar timer anterior
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Debounce de 400ms
    debounceTimerRef.current = setTimeout(() => {
      searchCie10(newValue);
    }, 400);

    // Notificar cambio
    if (onChange) {
      onChange(newValue, null);
    }
  };

  // Seleccionar una sugerencia
  const handleSelectSuggestion = (suggestion) => {
    // Una búsqueda pendiente reabriría el desplegable y pisaría la selección.
    cancelarBusqueda();

    if (suggestion.isManual) {
      // Modo entrada manual
      setIsManualEntry(true);
      setShowDropdown(false);
      setSelectedCode(null);
      setHighlightedIndex(-1);
      inputRef.current?.focus();
      return;
    }

    // Seleccionar código CIE-10
    const displayText = `[${suggestion.code}] ${suggestion.description}`;
    setInputValue(displayText);
    setSelectedCode(suggestion);
    setIsManualEntry(false);
    setShowDropdown(false);
    setHighlightedIndex(-1);

    if (onChange) {
      onChange(displayText, suggestion);
    }
  };

  // Limpiar selección
  const handleClear = () => {
    cancelarBusqueda();
    setInputValue('');
    setSelectedCode(null);
    setIsManualEntry(false);
    setSuggestions([]);
    setShowDropdown(false);
    setHighlightedIndex(-1);
    
    if (onChange) {
      onChange('', null);
    }
    
    inputRef.current?.focus();
  };

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) {
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleSelectSuggestion(suggestions[highlightedIndex]);
        }
        break;
      
      case 'Escape':
        e.preventDefault();
        setShowDropdown(false);
        setHighlightedIndex(-1);
        break;
      
      default:
        break;
    }
  };

  // Estilos condicionales
  const inputClasses = `
    w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent
    ${
      disabled
        ? 'bg-gray-100 cursor-not-allowed text-gray-500'
        : selectedCode
        ? 'bg-green-50 border-green-500'
        : isManualEntry
        ? 'bg-yellow-50 border-yellow-500'
        : 'bg-white border-gray-300'
    }
  `;

  return (
    <div className="relative">
      {/* Label */}
      {label && (
        <label 
          htmlFor={inputId}
          className="block text-sm font-medium text-gray-700 mb-2"
        >
          {label}
          {required && <span className="text-red-600 ml-1">*</span>}
        </label>
      )}

      {/* Input Container */}
      <div className="relative">
        <input
          ref={inputRef}
          id={inputId}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) {
              setShowDropdown(true);
            }
          }}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          className={inputClasses}
          role="combobox"
          aria-expanded={showDropdown}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-controls={listboxId}
          aria-activedescendant={
            highlightedIndex >= 0 && showDropdown
              ? optionId(highlightedIndex)
              : undefined
          }
        />

        {/* Íconos de estado */}
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
          {loading && <Loader className="h-5 w-5 text-blue-600 animate-spin" />}
          
          {!loading && inputValue && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600 transition"
              aria-label="Limpiar selección"
            >
              <X className="h-5 w-5" />
            </button>
          )}
          
          {!loading && !inputValue && <Search className="h-5 w-5 text-gray-400" />}
        </div>
      </div>

      {/* Indicadores de modo */}
      {selectedCode && (
        <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
          <span className="font-medium">✅ Código CIE-10 seleccionado:</span>
          <span className="font-mono">{selectedCode.code}</span>
        </p>
      )}
      
      {isManualEntry && (
        <p className="text-xs text-yellow-600 mt-1 flex items-center gap-1">
          <span className="font-medium">🖊️ Entrada manual:</span>
          <span>Sin código CIE-10 asociado</span>
        </p>
      )}

      {/* Dropdown de sugerencias */}
      {showDropdown && suggestions.length > 0 && (
        <div
          ref={dropdownRef}
          id={listboxId}
          role="listbox"
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-y-auto"
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={suggestion._id || index}
              id={optionId(index)}
              type="button"
              role="option"
              aria-selected={highlightedIndex === index}
              onClick={() => handleSelectSuggestion(suggestion)}
              className={`
                w-full px-4 py-3 text-left hover:bg-blue-50 transition border-b border-gray-100 last:border-b-0
                ${
                  suggestion.isManual
                    ? 'bg-yellow-50 hover:bg-yellow-100 font-medium text-yellow-800'
                    : highlightedIndex === index
                    ? 'bg-blue-100'
                    : ''
                }
              `}
            >
              {suggestion.isManual ? (
                <div className="flex items-center gap-2">
                  <span>🖊️</span>
                  <span>Otro (escribir manualmente)</span>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded font-semibold">
                      {suggestion.code}
                    </span>
                    {suggestion.type === 'symptom' && (
                      <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded">
                        Síntoma
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-800 mt-1">{suggestion.description}</p>
                  {suggestion.chapter && (
                    <p className="text-xs text-gray-500 mt-1">{suggestion.chapter}</p>
                  )}
                </>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mt-2 flex items-start gap-2 text-sm text-red-600" role="alert">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Ayuda */}
      {!disabled && !selectedCode && !isManualEntry && inputValue.length === 0 && (
        <p className="text-xs text-gray-500 mt-1">
          💡 Escribe al menos 2 caracteres para buscar en CIE-10 o selecciona "Otro" para entrada manual
        </p>
      )}
    </div>
  );
}

export default Cie10Autocomplete;
